<template>
    <div class="title-section">
        <span class="title-section__title">{{ pageTitle }}</span>
        <span class="title-section__subtitle">{{ pageSubtitle }}</span>
        <span
            v-if="isExcludedFromStats"
            class="title-section__muted"
        >
            {{ t('exports.viewReport.excludedFromStats') }}
        </span>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, toRefs } from 'vue'
import { ReportTypes } from '@/configuration/report/report-types'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { useGeneratorDetailsQuery } from '@/api/queries/generators/useGeneratorDetailsQuery'
import { useGeneratorReportGetQuery } from '@/api/queries/reports/useGeneratorReportGetQuery'

const props = defineProps<{
    reportNumber: string
    reportType: number
    generatorId: string
}>()

const { reportNumber, reportType, generatorId } = toRefs(props)

const { t } = useI18n()

const { data: reportData } = useGeneratorReportGetQuery(reportNumber, generatorId)
const { data: generatorDetailsData } = useGeneratorDetailsQuery(generatorId)

const isExcludedFromStats = computed(() => reportData.value?.isExcluded ?? false)

const reportDate = computed(() => reportData.value?.reports?.at(0)?.date)

const pageTitle = computed(() => t('exports.viewReport.title', { reportNumber: reportNumber.value ?? '' }))

const pageSubtitle = computed(() => {
    if (!generatorDetailsData.value) {
        return ''
    }

    const plantGroupName = generatorDetailsData.value.riverGroupName
    const plantName = generatorDetailsData.value.powerPlantName
    const generatorName = generatorDetailsData.value.name
    const generatorSid = generatorDetailsData.value.generatorID
    const reportTypeString = t(ReportTypes.find((e) => e.value === reportType.value)?.labelKey ?? '-')

    const reportDateFormatted = reportDate.value ? format(new Date(reportDate.value), DATE_FORMAT) : ''

    return `${plantGroupName} / ${plantName} / ${generatorName} / ${generatorSid} / ${reportTypeString} / ${reportDateFormatted}`
})
</script>

<style scoped lang="scss">
.title-section {
    margin: $spacing-s $spacing-s;

    display: flex;
    flex-direction: column;
    gap: $spacing-3xs;

    &__title {
        font-weight: 500;
    }

    &__muted {
        font-weight: 500;
        color: $pbl-text-muted;
    }
}
</style>
